import { useEffect, useState } from "react";
import { ArrowRight, Loader2, ShieldCheck, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { idAuth } from "../lib/idAuth";
import { getOAuthClientHostname } from "../lib/oauthClientDisplay";

type ConsentDetails = {
  authorization_id: string;
  redirect_uri: string;
  scope: string;
  client: { id: string; name: string; uri: string; logo_uri?: string | null };
};

const authorizationIdPattern = /^[A-Za-z0-9_-]{8,512}$/;

export const WerseeIdOAuthConsent = () => {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") || "";
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Authorize app - Wersee ID";
  }, []);

  useEffect(() => {
    if (!authorizationIdPattern.test(authorizationId)) {
      setError("This authorization request is invalid.");
      return;
    }
    void idAuth
      .oauthConsent(authorizationId)
      .then((data) => {
        if ("redirect_url" in data && typeof data.redirect_url === "string") {
          window.location.replace(data.redirect_url);
          return;
        }
        setDetails(data as ConsentDetails);
      })
      .catch((nextError: Error & { status?: number }) => {
        if (nextError.status === 401) {
          const login = new URL("/login", window.location.origin);
          login.searchParams.set("redirect", window.location.href);
          window.location.replace(login.toString());
          return;
        }
        setError(
          nextError.message ||
            "This authorization request is invalid or expired.",
        );
      });
  }, [authorizationId]);

  const decide = async (decision: "approve" | "deny") => {
    setBusy(true);
    setError(null);
    try {
      const result = await idAuth.decideOAuthConsent(authorizationId, decision);
      window.location.replace(result.redirect_url);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "The decision could not be saved.",
      );
      setBusy(false);
    }
  };

  const scopes = details?.scope.split(/\s+/).filter(Boolean) || [];
  return (
    <div className="relative grid min-h-[100dvh] place-items-center overflow-hidden bg-[#060608] px-4 py-10 text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(124,58,237,0.2),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(37,99,235,0.12),transparent_30%)]" />
      <main className="relative w-full max-w-md rounded-[28px] border border-white/10 bg-white/[0.055] p-6 shadow-[0_30px_100px_rgba(0,0,0,0.45)] backdrop-blur-2xl sm:p-8">
        <div className="mx-auto mb-6 flex w-fit items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-[14px] bg-white text-sm font-black text-black">
            W
          </span>
          <div>
            <p className="text-sm font-bold">Wersee ID</p>
            <p className="text-[10px] text-white/40">Secure OAuth consent</p>
          </div>
        </div>
        {error ? (
          <div className="space-y-4">
            <div
              role="alert"
              className="rounded-2xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-200"
            >
              {error}
            </div>
            <a
              href="/account/apps"
              className="flex w-full items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-bold text-black"
            >
              Connected apps
            </a>
          </div>
        ) : !details ? (
          <div className="flex min-h-48 items-center justify-center gap-3 text-sm text-white/45">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking request
          </div>
        ) : (
          <div className="space-y-5">
            <header className="text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center overflow-hidden rounded-2xl border border-white/10 bg-white/10">
                {details.client.logo_uri ? (
                  <img
                    src={details.client.logo_uri}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ShieldCheck className="h-7 w-7" />
                )}
              </div>
              <h1 className="mt-4 text-xl font-black">
                Continue to {details.client.name}
              </h1>
              <p className="mt-2 text-sm leading-6 text-white/45">
                This app is requesting access to your Wersee ID.
              </p>
            </header>
            <section className="rounded-2xl border border-white/10 bg-black/25 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/35">
                Requested access
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {scopes.map((scope) => (
                  <span
                    key={scope}
                    className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/65"
                  >
                    {scope}
                  </span>
                ))}
              </div>
              <p className="mt-4 break-all text-[11px] text-white/30">
                Return domain: {getOAuthClientHostname(details.redirect_uri)}
              </p>
            </section>
            <p className="text-xs leading-5 text-white/40">
              Approval creates a revocable app-specific Wersee session. Your
              password, refresh cookie and provider tokens are never shared with
              the app.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                disabled={busy}
                onClick={() => void decide("deny")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Deny
              </button>
              <button
                disabled={busy}
                onClick={() => void decide("approve")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-bold text-black disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                Approve
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
